import React from 'react';
import { MediaGridContext } from '../MediaGrid/MediaGrid.view';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import classNames from 'classnames';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { IconsEnum } from '../../../../constants/icons';
import { MediaItemSelectedType } from '../../../../models/media';

export type ActionButtonGroupViewProps = {
  context: MediaGridContext;
  primaryButtonAction?: CallableFunction;
  secondaryButtonAction?: CallableFunction;
  idsInvalidImagesSelected: MediaItemSelectedType[];
};

export type ButtonGroupProps = ActionButtonGroupViewProps & {
  breakpoint: BreakpointsValuesEnum;
  primaryButtonClasses: string;
  secondaryButtonClasses: string;
};

const ShareInPostButton = (props: {
  primaryButtonClasses: string;
  primaryButtonAction: CallableFunction;
}): JSX.Element => {
  return (
    <div className={props.primaryButtonClasses}>
      <Button
        action={() => props.primaryButtonAction()}
        type={'button'}
        variant={'solid'}
        size={'large'}
        color={ButtonColorsEnum.primary}
        icon={{ value: IconsEnum.share, side: 'left' }}>
        Share in Post
      </Button>
    </div>
  );
};

const AddToPostButton = (props: {
  breakpoint: BreakpointsValuesEnum;
  primaryButtonClasses: string;
  primaryButtonAction: CallableFunction;
}): JSX.Element => {
  return (
    <div className={props.primaryButtonClasses}>
      <Button
        action={() => props.primaryButtonAction()}
        type={'button'}
        variant={'solid'}
        size={'large'}
        color={ButtonColorsEnum.primary}
        icon={props.breakpoint === BreakpointsValuesEnum.small ? { value: IconsEnum.share, side: 'left' } : undefined}>
        Add to Post
      </Button>
    </div>
  );
};

const AddToLibraryButton = (props: {
  showForSmallOnly?: boolean;
  secondaryButtonClasses: string;
  secondaryButtonAction: CallableFunction;
}): JSX.Element => {
  const secondaryButtonClasses = classNames([props.secondaryButtonClasses], {
    'show-for-small-only': props.showForSmallOnly
  });

  return (
    <div className={secondaryButtonClasses}>
      <Button
        type={'button'}
        variant={'hollow'}
        size={'large'}
        color={ButtonColorsEnum.primary}
        icon={{ value: IconsEnum.plus, side: 'left' }}
        action={() => props.secondaryButtonAction()}>
        Add to Library
      </Button>
    </div>
  );
};

const DeleteFromLibraryButton = (props: {
  secondaryButtonClasses: string;
  secondaryButtonAction: CallableFunction;
}): JSX.Element => {
  return (
    <div className={props.secondaryButtonClasses}>
      <Button
        type={'button'}
        variant={'hollow'}
        size={'large'}
        color={ButtonColorsEnum.alert}
        icon={{ value: IconsEnum.trash, side: 'left' }}
        action={() => props.secondaryButtonAction()}>
        Delete
      </Button>
    </div>
  );
};

// Used for Media Library Page & Modal
const PageActionButtonGroup = (props: ButtonGroupProps): JSX.Element => {
  return (
    <>
      {props.secondaryButtonAction && (
        <DeleteFromLibraryButton
          secondaryButtonClasses={props.secondaryButtonClasses}
          secondaryButtonAction={props.secondaryButtonAction}
        />
      )}
      {props.primaryButtonAction && props.idsInvalidImagesSelected.length === 0 && (
        <ShareInPostButton
          primaryButtonClasses={props.primaryButtonClasses}
          primaryButtonAction={props.primaryButtonAction}
        />
      )}
    </>
  );
};

// Used for Media Library Page & Modal
const PostEditorMediaLibraryActionButtonGroup = (props: ButtonGroupProps): JSX.Element => {
  return (
    <>
      {props.primaryButtonAction && !props.secondaryButtonAction && (
        <AddToPostButton
          breakpoint={props.breakpoint}
          primaryButtonClasses={props.primaryButtonClasses}
          primaryButtonAction={props.primaryButtonAction}
        />
      )}
      {props.secondaryButtonAction && (
        <DeleteFromLibraryButton
          secondaryButtonClasses={props.primaryButtonClasses}
          secondaryButtonAction={props.secondaryButtonAction}
        />
      )}
    </>
  );
};

// Used for Find Images Modal via Post Editor
const PostEditorFindImagesActionButtonGroup = (props: ButtonGroupProps): JSX.Element => {
  return (
    <>
      {props.secondaryButtonAction && (
        <AddToLibraryButton
          secondaryButtonClasses={props.secondaryButtonClasses}
          secondaryButtonAction={props.secondaryButtonAction}
          showForSmallOnly={true}
        />
      )}
      {props.primaryButtonAction && (
        <AddToPostButton
          breakpoint={props.breakpoint}
          primaryButtonClasses={props.primaryButtonClasses}
          primaryButtonAction={props.primaryButtonAction}
        />
      )}
    </>
  );
};

// Used for Find Images on Media Library
const FindImagesActionButtonGroup = (props: ButtonGroupProps): JSX.Element => {
  return (
    <>
      {props.secondaryButtonAction && (
        <AddToLibraryButton
          secondaryButtonClasses={props.secondaryButtonClasses}
          secondaryButtonAction={props.secondaryButtonAction}
        />
      )}
      {props.primaryButtonAction && (
        <ShareInPostButton
          primaryButtonClasses={props.primaryButtonClasses}
          primaryButtonAction={props.primaryButtonAction}
        />
      )}
    </>
  );
};

export const ActionButtonGroupView = (props: ActionButtonGroupViewProps): JSX.Element => {
  const breakpoint: keyof typeof BreakpointsValuesEnum = useBreakpoints();
  const primaryButtonClasses = classNames({
    'small-order-2 medium-order-1': breakpoint === 'small'
  });
  const secondaryButtonClasses = classNames({
    'small-order-1 medium-order-2': breakpoint === 'small'
  });

  const buttonGroupProps: ButtonGroupProps = {
    ...props,
    breakpoint: BreakpointsValuesEnum[breakpoint],
    primaryButtonClasses,
    secondaryButtonClasses,
    idsInvalidImagesSelected: props.idsInvalidImagesSelected
  };

  switch (props.context) {
    case MediaGridContext.PAGE:
    case MediaGridContext.MODAL:
      return <PageActionButtonGroup {...buttonGroupProps} />;
    case MediaGridContext.FIND_IMAGES:
      return <FindImagesActionButtonGroup {...buttonGroupProps} />;
    case MediaGridContext.POST_EDITOR_MEDIA_LIBRARY:
      return <PostEditorMediaLibraryActionButtonGroup {...buttonGroupProps} />;
    case MediaGridContext.POST_EDITOR_FIND_IMAGES:
      return <PostEditorFindImagesActionButtonGroup {...buttonGroupProps} />;
    default:
      return <PageActionButtonGroup {...buttonGroupProps} />;
  }
};
